import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getUsers } from '@/services/users';
import { CohortMember, addCohortMember, getCohortMembers, removeCohortMember } from '@/services/cohorts';

// The admin is hardwired to the dark theme; these inline styles can't read the
// styled-theming palette tokens (they need a styled-component context), so the
// dark values are mirrored here to keep text readable on the dark modal.
const TEXT = '#dddddd';
const TEXT_DIM = '#999999';
const PANEL_BG = '#2a2a31';
const PANEL_BORDER = '#3a3a42';
const CHIP_BG = '#33333b';
const ACCENT = '#4ce1b6';
const DANGER = '#ff6b6b';

interface UserResult {
    id: string;
    email: string;
}

interface CohortMembersProps {
    // Present in EDIT mode → add/remove hit the API immediately (enrol + grant).
    // Absent in CREATE mode → members are STAGED and reported via onStagedChange;
    // the modal commits them after the cohort is created (it has an id then).
    cohortId?: string;
    onStagedChange?: (members: CohortMember[]) => void;
    // Reports the live roster size up (edit + create) so the modal's "Current attendees"
    // count and capacity-floor validation stay in sync as members are added/removed.
    onCountChange?: (count: number) => void;
}

/**
 * Admin-curated cohort roster. Members are picked by searching users by email
 * (DB-backed, no free typing) so there are no typos — the same reasoning as the
 * timezone picker. In EDIT mode, adding a member enrols them AND grants a year of
 * Premium + Course 33 immediately (server-side), so add/remove hit the API at
 * once. In CREATE mode (no cohortId yet) selections are staged locally and the
 * modal commits them once the cohort exists.
 */
const CohortMembers = ({ cohortId, onStagedChange, onCountChange }: CohortMembersProps) => {
    const queryClient = useQueryClient();
    const [members, setMembers] = useState<CohortMember[]>([]);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<UserResult[]>([]);
    const [searching, setSearching] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Monotonic token so a slow earlier search response can't clobber a newer query's results.
    const searchSeq = useRef(0);

    // Load the current roster once the cohort id is known (edit mode only).
    useEffect(() => {
        if (!cohortId) return;
        let active = true;
        (async () => {
            const res = await getCohortMembers(cohortId);
            if (active && res.status === 'success' && res.data) {
                setMembers(res.data.members);
                onCountChange?.(res.data.members.length);
            }
        })();
        return () => {
            active = false;
        };
    }, [cohortId, onCountChange]);

    // Debounced email search. Already-enrolled matches are KEPT in the results and
    // shown as "Already in cohort" below — filtering them out made an existing member
    // fall through to the misleading "No matching users." empty state.
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            setSearching(false);
            return;
        }
        setSearching(true);
        const seq = ++searchSeq.current;
        debounceRef.current = setTimeout(async () => {
            const res = await getUsers(1, 8, { search: q });
            if (seq !== searchSeq.current) return; // a newer search has superseded this one
            if (res.status === 'success' && res.data) {
                setResults(res.data.users.map((u) => ({ id: u.id, email: u.email })));
            } else {
                setResults([]);
            }
            setSearching(false);
        }, 350);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query]);

    // Apply a change via the functional updater (safe against unflushed state) and,
    // in staging mode, report the new list up so the modal can commit it after create.
    const commit = (update: (prev: CohortMember[]) => CohortMember[]) => {
        setMembers((prev) => {
            const next = update(prev);
            if (!cohortId) onStagedChange?.(next);
            onCountChange?.(next.length);
            return next;
        });
    };

    const handleAdd = async (user: UserResult) => {
        setError(null);
        if (members.some((x) => x.userId === user.id)) return;
        const member: CohortMember = { userId: user.id, email: user.email };
        if (cohortId) {
            setBusyId(user.id);
            const res = await addCohortMember(cohortId, user.id);
            setBusyId(null);
            if (res.status === 'error') {
                setError(res.error?.message ?? 'Failed to add the member.');
                return;
            }
            // Server bumped currentAttendeeCount — refresh the list table's capacity column.
            // (Not ['cohort', id]: that would reset the open modal's unsaved form.)
            queryClient.invalidateQueries({ queryKey: ['cohorts'] });
        }
        commit((prev) => (prev.some((x) => x.userId === member.userId) ? prev : [...prev, member]));
        setQuery('');
        setResults([]);
    };

    const handleRemove = async (userId: string) => {
        setError(null);
        // EDIT mode: removal fires immediately server-side (enrollment drop) — confirm it.
        // CREATE mode: the member is only staged locally, so un-staging needs no confirm
        // (and the "loses access / grant kept" warning isn't true yet).
        if (
            cohortId &&
            !window.confirm('Remove this member from the cohort? They lose live-session join access. Their Course-33/Premium grant is kept.')
        )
            return;
        if (cohortId) {
            setBusyId(userId);
            const res = await removeCohortMember(cohortId, userId);
            setBusyId(null);
            if (res.status === 'error') {
                setError(res.error?.message ?? 'Failed to remove the member.');
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['cohorts'] });
        }
        commit((prev) => prev.filter((x) => x.userId !== userId));
    };

    return (
        <div>
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search users by email…"
                    autoComplete="off"
                    style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '10px 12px',
                        borderRadius: 4,
                        border: `1px solid ${PANEL_BORDER}`,
                        background: PANEL_BG,
                        color: TEXT,
                        fontSize: 14,
                    }}
                />
                {query.trim().length >= 2 && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 20,
                            marginTop: 4,
                            maxHeight: 220,
                            overflowY: 'auto',
                            borderRadius: 4,
                            border: `1px solid ${PANEL_BORDER}`,
                            background: PANEL_BG,
                            boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
                        }}
                    >
                        {searching && <div style={{ padding: '10px 12px', color: TEXT_DIM, fontSize: 13 }}>Searching…</div>}
                        {!searching && results.length === 0 && (
                            <div style={{ padding: '10px 12px', color: TEXT_DIM, fontSize: 13 }}>No matching users.</div>
                        )}
                        {!searching &&
                            results.map((u) => {
                                // Already on the roster → show a non-clickable "Already in cohort"
                                // row so the admin understands why they can't re-add (instead of
                                // the old behavior that hid them and read "No matching users.").
                                if (members.some((m) => m.userId === u.id)) {
                                    return (
                                        <div
                                            key={u.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 8,
                                                width: '100%',
                                                padding: '10px 12px',
                                                borderBottom: `1px solid ${PANEL_BORDER}`,
                                                color: TEXT_DIM,
                                                fontSize: 13,
                                            }}
                                        >
                                            <span>{u.email || u.id}</span>
                                            <span style={{ color: ACCENT, fontSize: 12, whiteSpace: 'nowrap' }}>Already in cohort</span>
                                        </div>
                                    );
                                }
                                return (
                                    <button
                                        key={u.id}
                                        type="button"
                                        onClick={() => handleAdd(u)}
                                        disabled={busyId === u.id}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            textAlign: 'left',
                                            padding: '10px 12px',
                                            border: 'none',
                                            borderBottom: `1px solid ${PANEL_BORDER}`,
                                            background: 'transparent',
                                            color: TEXT,
                                            cursor: busyId === u.id ? 'default' : 'pointer',
                                            fontSize: 13,
                                            opacity: busyId === u.id ? 0.6 : 1,
                                        }}
                                    >
                                        {u.email || u.id}
                                    </button>
                                );
                            })}
                    </div>
                )}
            </div>

            {error && <p style={{ color: DANGER, fontSize: 12, margin: '8px 0 0' }}>{error}</p>}

            {members.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                    {members.map((m) => (
                        <span
                            key={m.userId}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '6px 10px',
                                borderRadius: 16,
                                background: CHIP_BG,
                                border: `1px solid ${PANEL_BORDER}`,
                                color: TEXT,
                                fontSize: 13,
                            }}
                        >
                            {m.email || m.userId}
                            <button
                                type="button"
                                onClick={() => handleRemove(m.userId)}
                                disabled={busyId === m.userId}
                                aria-label={`Remove ${m.email || m.userId}`}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: busyId === m.userId ? TEXT_DIM : ACCENT,
                                    cursor: busyId === m.userId ? 'default' : 'pointer',
                                    fontSize: 16,
                                    lineHeight: 1,
                                    padding: 0,
                                }}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            ) : (
                <p style={{ color: TEXT_DIM, fontSize: 13, marginTop: 12 }}>No members yet. Search above to add users.</p>
            )}
        </div>
    );
};

export default CohortMembers;
